import querystring from 'querystring';
import type { GetServerSideProps, NextPage } from 'next';
import { getApolloSsrClient } from '@stitch-fix/sf-next/server';
import { z } from 'zod';
import Script from 'next/script';
import { modularPageWrapperSchema } from '../../components/ModularPageWrapper/schema';
import { getCachedEntry } from '../../utils/requests/contentstack/cachedRequests';
import { formatError, serverLogger } from '../../utils/loggers/server';
import { ModularPageWrapper } from '../../components/ModularPageWrapper';
import {
  getReactivationUrl,
  isDynamicAdsLandingPage,
} from '../../utils/redirects';
import {
  isAllSettledResponseFailure,
  logResponsesFromAllSettledSSR,
} from '../../utils/promises';
import { getDynamicAdsRequest } from '../../features/dynamicAds/utils/getDynamicAdsRequest';
import { getModulesWithHeroOverride } from '../../features/dynamicAds/utils/getModulesWithHeroOverride';
import {
  createDynamicAdGrouping,
  setDynamicProductAdCookie,
} from '../../features/dynamicAds/utils/dynamicAdsGrouping';
import { getHighIntentAllocation } from '../../features/dynamicAds/utils/highIntentAllocation';
import { getExperimentHeroProps } from '../getExperimentHeroProps';

export type WomenPageProps = z.infer<typeof modularPageWrapperSchema>;

export const getServerSideProps: GetServerSideProps<WomenPageProps> = async ({
  req,
  res,
  query,
}) => {
  const url = req.url || '';
  const stringifiedQuery = querystring.stringify(query);

  const reactivationUrl = getReactivationUrl(url);

  if (reactivationUrl) {
    return {
      redirect: {
        destination: `${reactivationUrl}?${stringifiedQuery}`,
        permanent: false,
      },
    };
  }

  try {
    const entryRequest = await getCachedEntry({
      contentType: 'modular_page',
      uid: 'blt804f82dd5e51b628',
      schema: modularPageWrapperSchema,
      logger: serverLogger,
    });

    let dynamicAdsRequest: ReturnType<typeof getDynamicAdsRequest> | null =
      null;
    let isAllocatedToHighIntent = false;
    let isLoggedIn = false;
    const apolloSsrClient = getApolloSsrClient({ req });

    // only call the dynamic ads algos if the page is a dynamic ads landing page
    if (isDynamicAdsLandingPage(url)) {
      const allocationResult = await getHighIntentAllocation({
        apolloSsrClient,
        req,
        queryParams: query,
      });

      isAllocatedToHighIntent = allocationResult.qualifiesForGroupingLP;
      isLoggedIn = allocationResult.isLoggedIn;

      dynamicAdsRequest = getDynamicAdsRequest({
        req,
        dynamicAdsContext: allocationResult.qualifiesForGroupingLP
          ? { variant: 'GROUPING_LP' }
          : undefined,
      });
    }

    /**
     * this allows us to handle a dynamic number of requests in a non-synchronous way
     */
    const responses = await Promise.allSettled([
      entryRequest,
      dynamicAdsRequest,
    ]);

    logResponsesFromAllSettledSSR({
      responses,
      message: 'women data error',
      team: 'client-onboarding',
    });

    const [entryResponse, dynamicAdsResponse] = responses;

    if (isAllSettledResponseFailure(entryResponse)) {
      throw entryResponse.reason;
    }

    let modules = entryResponse.value.data.modules;

    // Allocate the visitor to the hero experiment.
    // If they are allocated to the treatment the function will return the props we
    // should use for the hero, otherwise it will return null
    const experimentHeroProps = await getExperimentHeroProps({
      req,
      apolloSsrClient,
      businessLinePage: 'women',
    });

    // Override the hero module if we have experiment hero props
    if (experimentHeroProps) {
      modules = modules.map(module => {
        if (module.type === 'hero' || module.type === 'galleryHero') {
          return {
            type: 'hero',
            props: experimentHeroProps,
          };
        }

        return module;
      });
    }

    const dynamicAdsData =
      dynamicAdsResponse.status === 'fulfilled'
        ? dynamicAdsResponse.value?.data
        : undefined;

    // If the user is allocated to high intent, we need to create a cookie
    if (isAllocatedToHighIntent && !isLoggedIn) {
      setDynamicProductAdCookie({ req, res, dynamicAdsData });
    }
    if (isAllocatedToHighIntent && isLoggedIn) {
      await createDynamicAdGrouping({
        apolloSsrClient,
        dynamicAdsData,
        req,
      });
    }

    return {
      props: {
        seoData: entryResponse.value.data.seoData,
        modules: getModulesWithHeroOverride({
          businessLinePage: 'women',
          modules,
          dynamicAds: dynamicAdsData,
        }),
      },
    };
  } catch (error: unknown) {
    serverLogger({
      level: 'error',
      team: 'client-onboarding',
      message: 'Required page data is undefined - Women',
      context: {
        error: formatError(error),
        page: '/women',
        severity: 'critical',
      },
    });

    throw new Error('Required data not provided to render Women page');
  }
};

const WomenPage: NextPage<WomenPageProps> = ({ seoData, modules }) => {
  return (
    <>
      <Script
        // Blueshift web conversion integration (https://experiences.getblueshift.com/)
        data-testid="blueshift-script"
        src="https://www.lightboxcdn.com/vendor/3767d51a-4af9-4270-8ee4-c7310e4371b0/lightbox_speed.js"
        defer
      />
      <ModularPageWrapper seoData={seoData} modules={modules} />;
    </>
  );
};

export default WomenPage;
